import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { RouterModule } from '@angular/router';
import { CommonDirectives } from '@app/shared/directives/common-directives';
import { UserAuthService } from '../../services/userAuth.service';
import { AuthResponse } from '../../interfaces/authResponse.interface';
import { LoadSpinnerWrapperComponent } from '@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component';
import { AppState } from '@app/core/store/app.state';
import { Store } from '@ngrx/store';
import * as UserAuthActions from '@app/core/store/userAuth/userAuth.actions';
import {firstValueFrom, Subject, Subscription, take, takeUntil} from 'rxjs';
import { LogoutType } from '@app/core/enums/logout-types.enum';
import { MatIconModule } from '@angular/material/icon';
import {AmplifyAuthenticatorModule, AuthenticatorService} from "@aws-amplify/ui-angular";
import type { AuthenticatorMachineOptions } from '@aws-amplify/ui';
import { fetchAuthSession, signOut, signIn, SignInInput } from "aws-amplify/auth"
import { I18n as cognitoI18n } from 'aws-amplify/utils'
import {environment} from "@env/environment";
import {TokenStorageService} from "@app/core/services/token-storage.service";
import {AutoLogoutService} from "@app/core/services/auto-logout.service";

@Component({
  selector: 'app-portal-login',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    CommonDirectives,
    RouterModule,
    LoadSpinnerWrapperComponent,
    MatIconModule,
    AmplifyAuthenticatorModule
],
  templateUrl: './portal-login.component.html',
  styleUrl: './portal-login.component.scss'
})
export class PortalLoginComponent implements OnInit, OnDestroy {
  ngDestroy$ = new Subject();
  loading = false;
  showBannerFlag = false;
  private subscriptions = new Subscription();

  loginForm = this.fb.group({
    userName: ['', Validators.required],
    password: ['', Validators.required]
  });

  cognitoServices: AuthenticatorMachineOptions['services'] = {
    handleSignIn: async (input: SignInInput) => {
      const { username, password, options } = input;
      const customUsername = username.toLowerCase();
      return signIn({
        username: customUsername,
        password,
        options: options
      });
    },
    handleForgotPassword: async ({ username }) => {
      cognitoI18n.putVocabulariesForLanguage('en', {
        'Reset Password': `If an account exists for ${username}, a password reset code has been sent. Please check your inbox, as well as your spam, junk, or promotions folders.`
      });

      const response = await firstValueFrom(this.userAuthService.forgotPassword(username.toLowerCase()));
      if (!response.succeeded) {
        throw new Error(response.errorMessages?.join(' ') || 'Unable to send a password reset code.');
      }

      return {
        isPasswordReset: false,
        nextStep: {
          resetPasswordStep: 'CONFIRM_RESET_PASSWORD_WITH_CODE' as const,
          codeDeliveryDetails: {
            deliveryMedium: 'EMAIL' as const
          }
        }
      };
    },
    handleForgotPasswordSubmit: async ({ username, confirmationCode, newPassword }) => {
      const response = await firstValueFrom(this.userAuthService.resetPassword(username, newPassword, confirmationCode));
      if (!response.succeeded) {
        throw new Error(response.errorMessages?.join(' ') || 'Unable to reset password.');
      }
    }
  };

  cognitoFormFields = {
    signIn: {
      username: {
        placeholder: 'Email*',
        isRequired: true,
        label: ''
      },
      password: {
        placeholder: 'Password*',
        isRequired: true,
        label: '',
        autocomplete: 'new-password'
      }
    },
    forgotPassword: {
      username: {
        placeholder: 'Email*',
        isRequired: true,
        label: '',
        autocomplete: 'off'
      },
    },
    confirmResetPassword: {
      confirmation_code: {
        autocomplete: 'one-time-code'
      },
      password: {
        autocomplete: 'new-password',
      },
      confirm_password: {
        autocomplete: 'new-password'
      }
    },
    setupTotp: {
      QR: {
        totpIssuer: environment.cognitoTotpIssuer
      },
      confirmation_code: {
        label: 'Code *',
        placeholder: 'Enter the code without spaces',
        isRequired: true
      }
    }
  }

  constructor(
    private fb: FormBuilder,
    private userAuthService: UserAuthService,
    private store: Store<AppState>,
    private authenticator: AuthenticatorService,
    private tokenStorageService: TokenStorageService,
    private autoLogoutService: AutoLogoutService
  ) {}

  ngOnDestroy(): void {
    this.ngDestroy$.next(true);
    this.subscriptions.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    this.showBanner();
    this.subscribeToFormChanges();
    await this.configureCognitoLogin();
  }

  subscribeToFormChanges(): void {
    this.loginForm.valueChanges
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((values) => {
        this.showBannerFlag = false;
    });
  }

  login() {
    const { userName, password } = this.loginForm.value;
    if (userName && password) {
      this.loading = true;
      // Call the login service with the provided credentials
      this.userAuthService.loginUser(userName, password)
        .subscribe((response: AuthResponse) => {
          this.loading = false;
          if (response.succeeded) {
            this.store.dispatch(UserAuthActions.LoginSuccess({ response }));
          } else {
            const data = [userName];
            if (response.session) { data.push(response.session); }
            this.store.dispatch(UserAuthActions.LoginFailure(
              {
                error: response.errorMessage ?? 'Login failed. Please try again!',
                data
              }
            ));
          }
        });
    }
  }

  public async listenForCognitoAuthToken(): Promise<void> {
    try {
      const cognitoSession = await fetchAuthSession();
      if(!cognitoSession.tokens?.idToken) {
        return;
      }
      this.userAuthService
        .validateCognitoTokens(cognitoSession.tokens.accessToken.toString(), cognitoSession.tokens.idToken.toString())
        .pipe(take(1))
        .subscribe((response: AuthResponse) => {
          this.loading = false;
          if (response.succeeded) {
            this.store.dispatch(UserAuthActions.LoginSuccess({ response }));
          } else {
            const data = [];
            const userName = cognitoSession.tokens?.signInDetails?.loginId;
            if(userName) data.push(userName);
            if (response.session) { data.push(response.session); }
            this.store.dispatch(UserAuthActions.LoginFailure(
              {
                error: response.errorMessage ?? 'Login failed. Please try again!',
                data
              }
            ));
          }
        });

    } catch(err) {
      console.log(err);
    }
  }
  private showBanner(): void {
    // Check for logout type in local storage to show appropriate banner. Navigation state does not work for some reason.
    if (localStorage.getItem('logoutType')) {
      const logoutType = localStorage.getItem('logoutType') as LogoutType;
      switch (logoutType) {
        case LogoutType.INACTIVITY:
          this.showBannerFlag = true;
          break;
        default:
          break;
      }
      localStorage.removeItem('logoutType');
    }
  }

  private async configureCognitoLogin() {
    const sessionExpired = this.autoLogoutService.isSessionExpired();
    if (sessionExpired) {
      this.autoLogoutService.stopWatching();
      this.autoLogoutService.clearLastActivity();
      this.tokenStorageService.clearStorage();
      localStorage.setItem('logoutType', LogoutType.INACTIVITY);
      localStorage.setItem('logout-time', Date.now().toString());
      await signOut();
      this.showBanner();
    }

    cognitoI18n.putVocabulariesForLanguage('en', {
      'Sign in': 'Login',
      'Forgot your password?': 'Forgot password?',
      'Confirm TOTP Code': 'Please enter the Two-Factor Authentication code generated by your authenticator app (without spaces).',
      'Setup TOTP': 'Scan with your authenticator app:',
      'Reset Password': 'If an account exists for your email address, a password reset code has been sent. Please check your inbox, as well as your spam, junk, or promotions folders.',
      'Password must have at least 8 characters': 'Please verify password meets all minimum requirements'
    });

    this.subscriptions.add(this.authenticator.subscribe(
      async (authState) =>  {
        switch (authState.authStatus) {
          case "authenticated":
            await this.listenForCognitoAuthToken();
            break;
          case "unauthenticated":
            this.loading = authState.isPending;
            break;
        }
      }
    ));
  }
}
